import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { Search, X, ChevronRight } from 'lucide-react';

/**
 * Compact, inline "combobox" — a button trigger that opens a search
 * dropdown of items.  Panel is rendered through a React Portal into
 * ``document.body`` with fixed positioning so it never gets clipped by
 * parent containers that have ``overflow-hidden`` (cards, table wrappers,
 * scrollable regions, etc.).
 *
 * Generic contract: items must have a stable ``id`` field; label + optional
 * sublabel are derived via callbacks so any shape of row works.
 */
export interface SearchPickerProps<T> {
  items: T[];
  selectedId: string | null;
  onPick: (item: T | null) => void;
  labelFor: (item: T) => string;
  sublabelFor?: (item: T) => string | undefined;
  placeholder?: string;
  emptyLabel?: string;
  testId?: string;
  disabled?: boolean;
  /** Compact mode reduces vertical padding for use inside table rows. */
  compact?: boolean;
  /** Maximum number of items rendered in the panel (default: 200). */
  maxRender?: number;
}

interface PanelPos { left: number; top: number; width: number; placement: 'below' | 'above' }

export function SearchPicker<T extends { id: string }>({
  items, selectedId, onPick, labelFor, sublabelFor,
  placeholder, emptyLabel, testId, disabled, compact, maxRender,
}: SearchPickerProps<T>): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<PanelPos | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const cap = maxRender ?? 200;
    const raw = q.trim().toLowerCase();
    if (!raw) return items.slice(0, cap);
    // Aggressive normaliser: lowercase + drop everything that isn't a
    // letter, digit or unicode word-ish char. That way "wierda park" and
    // "wierdapark" collapse to the same haystack; "SIT-7622" and "SIT
    // 7622" also match; punctuation drift ("Steers/Debonairs" vs "Steers
    // Debonairs") stops mattering.
    const norm = (s: string): string => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
    // Split the needle on whitespace so a user typing "goldwagen wierda"
    // still finds "Goldwagen wierdapark Ref 1552362" — every token must
    // hit somewhere in the label + sublabel.
    const tokens = raw.split(/\s+/).map(norm).filter(Boolean);
    if (tokens.length === 0) return items.slice(0, cap);
    return items.filter((x) => {
      const hay = norm(`${labelFor(x)} ${sublabelFor?.(x) || ''}`);
      return tokens.every((t) => hay.includes(t));
    }).slice(0, cap);
  }, [items, q, labelFor, sublabelFor, maxRender]);

  const selected = items.find((x) => x.id === selectedId) || null;

  // ---- Compute panel position (flip above when there's no room below) ----
  const computePos = (): void => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const wantedH = 460; // approx panel max height incl. search + list
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    const placement: 'below' | 'above' = (spaceBelow < wantedH && spaceAbove > spaceBelow) ? 'above' : 'below';
    setPos({
      left: r.left,
      top: placement === 'below' ? r.bottom + 4 : Math.max(8, r.top - wantedH - 4),
      width: Math.max(r.width, 280),
      placement,
    });
  };

  // Recompute on open + on any layout change while open.
  useLayoutEffect(() => { if (open) computePos(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onLayout = (): void => computePos();
    window.addEventListener('scroll', onLayout, true);
    window.addEventListener('resize', onLayout);
    return () => {
      window.removeEventListener('scroll', onLayout, true);
      window.removeEventListener('resize', onLayout);
    };
  }, [open]);

  // Close when clicking outside the trigger OR the portalled panel.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Close on Escape for keyboard users.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const triggerCls = compact
    ? 'h-7 text-xs px-2 py-1'
    : 'text-sm px-3 py-2 min-h-[38px]';

  const panel = open && pos ? createPortal(
    <div
      ref={panelRef}
      className="rounded-md border border-border/60 bg-white shadow-xl"
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        width: Math.min(640, Math.max(pos.width, 320)),
        zIndex: 9999,
      }}
      data-testid={testId ? `${testId}-panel` : undefined}
    >
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
        <Input
          autoFocus
          className="h-9 text-sm pl-7 border-0 focus:ring-0"
          placeholder="Type to search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid={testId ? `${testId}-search` : undefined}
        />
      </div>
      <div className="max-h-[420px] overflow-auto border-t border-border/40 divide-y divide-border/40">
        {filtered.length === 0 && (
          <div className="px-3 py-3 text-sm text-raptor-navy/50 italic">{emptyLabel || 'No matches.'}</div>
        )}
        {filtered.map((x) => (
          <button
            key={x.id}
            type="button"
            onClick={() => { onPick(x); setOpen(false); setQ(''); }}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-raptor-blue/5 ${selectedId === x.id ? 'bg-raptor-blue/10 border-l-4 border-raptor-blue' : ''}`}
            data-testid={testId ? `${testId}-option-${x.id}` : undefined}
          >
            <div className={`font-semibold text-raptor-navy ${compact ? 'text-xs' : ''}`}>{labelFor(x)}</div>
            {sublabelFor && (
              <div className="text-[11px] text-raptor-navy/60">{sublabelFor(x)}</div>
            )}
          </button>
        ))}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div className="relative" data-testid={testId} ref={triggerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`w-full text-left rounded-md border border-border/60 bg-white font-body flex items-center gap-2 focus:ring-2 focus:ring-raptor-blue/40 focus:outline-none ${triggerCls} ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-raptor-blue/40'}`}
        data-testid={testId ? `${testId}-button` : undefined}
      >
        {selected ? (
          <span className="flex-1 min-w-0">
            <div className={`font-semibold text-raptor-navy truncate ${compact ? 'text-xs' : ''}`}>{labelFor(selected)}</div>
            {!compact && sublabelFor && <div className="text-[10px] text-raptor-navy/60 truncate">{sublabelFor(selected)}</div>}
          </span>
        ) : (
          <span className="flex-1 text-raptor-navy/40 truncate">{placeholder || 'Pick one…'}</span>
        )}
        {selected && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPick(null); setQ(''); }}
            className="text-raptor-navy/40 hover:text-red-600"
            aria-label="Clear"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X size={compact ? 12 : 14} />
          </button>
        )}
        <ChevronRight size={12} className={`text-raptor-navy/40 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {panel}
    </div>
  );
}

export default SearchPicker;
